import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { billingQueryKeyPrefix } from '@/data/billing/query-keys';

export type InstallationSettingsUpdate = {
  installationId: number;
  stripeAutomaticallyAssignAndRemoveSeats?: boolean;
  stripeAutomaticallyAddSeatsOnUsageLimit?: boolean;
  flexCapacityEnabled?: boolean;
  /** Spend cap in cents per quota window; null clears the cap (unlimited). */
  flexCapacityMaxCentsPerPeriod?: number | null;
  flexCapacityTopUpLines?: number;
};

export function useUpdateInstallationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: InstallationSettingsUpdate) => {
      const response = await fetch('/api/settings/installation', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update installation settings');
      }

      return data;
    },
    onMutate: async (variables) => {
      if (!variables.installationId) {
        return { previousInstallationData: null };
      }

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['installation', variables.installationId],
        exact: true,
      });

      // Snapshot the previous value
      const previousInstallationData = queryClient.getQueryData([
        'installation',
        variables.installationId,
      ]);

      // Optimistically update to the new value
      const optimisticUpdate = Object.fromEntries(
        Object.entries({
          stripeAutomaticallyAssignAndRemoveSeats:
            variables.stripeAutomaticallyAssignAndRemoveSeats,
          stripeAutomaticallyAddSeatsOnUsageLimit:
            variables.stripeAutomaticallyAddSeatsOnUsageLimit,
          flexCapacityEnabled: variables.flexCapacityEnabled,
          flexCapacityMaxCentsPerPeriod: variables.flexCapacityMaxCentsPerPeriod,
          flexCapacityTopUpLines: variables.flexCapacityTopUpLines,
        }).filter(([, value]) => value !== undefined),
      );

      if (variables.flexCapacityEnabled === true) {
        // Server-side, enabling flex turns the legacy auto-seat toggle off.
        optimisticUpdate.stripeAutomaticallyAddSeatsOnUsageLimit = false;
      }

      if (previousInstallationData) {
        queryClient.setQueryData(['installation', variables.installationId], {
          ...(previousInstallationData as object),
          ...optimisticUpdate,
        });
      }

      return { previousInstallationData };
    },
    onSuccess: (_, variables) => {
      toast.success('Installation settings updated');
      if (!variables.installationId) {
        return undefined;
      }
      void queryClient.invalidateQueries({
        queryKey: ['installation', variables.installationId],
        exact: true,
      });
      const changesFlex =
        variables.flexCapacityEnabled !== undefined ||
        variables.flexCapacityMaxCentsPerPeriod !== undefined ||
        variables.flexCapacityTopUpLines !== undefined;
      // The usage payload carries the flex setting. Returned so the mutation stays pending until it
      // reloads, and so it reloads even if the editor unmounted during the save.
      return changesFlex
        ? queryClient.invalidateQueries({
            queryKey: [
              ...billingQueryKeyPrefix(),
              'codex-rate-limit-usage',
              variables.installationId,
            ],
          })
        : undefined;
    },
    onError: (error, variables, context) => {
      console.error('Installation settings update error:', error);
      if (context?.previousInstallationData && variables.installationId) {
        queryClient.setQueryData(
          ['installation', variables.installationId],
          context.previousInstallationData,
        );
      }
      toast.error('Failed to update installation settings');
    },
  });
}
